class PackManager {
  constructor() {
    this.PACK_BUILDER_URL = "/collections/" + (window.packCollectionHandle || "create-your-own-pack");
    this.PACK_KEY_BASE = "graeter_flavor_pack";
    this.collectionId = this.getCollectionId();
    this.currentProductVariantIds = this.getCurrentProductVariantIds();
    this.state = this.loadState();
    this.isFlavorPickerActive = false;
    this.packSizes = [];
    this.extractPackSizes();
    this.init();
  }
  getCollectionId() {
    return window.packCollectionId || "";
  }
  getCollectionHandle() {
    return window.packCollectionHandle || "default";
  }
  getCurrentProductVariantIds() {
    if (window.location.pathname.includes('/products/')) {
      const variantIds = [];
      if (window.product && window.product.variants) {
        window.product.variants.forEach(variant => {
          variantIds.push(String(variant.id));
        });
      }
      const currentVariantMeta = document.querySelector('meta[property="product:variant:id"]');
      if (currentVariantMeta) {
        variantIds.push(currentVariantMeta.content);
      }
      return variantIds;
    }
    return [];
  }
  extractPackSizes() {
    const packData = window.packProductVariants || [];
    if (packData.length > 0) {
      const sizes = new Set();
      packData.forEach((variant) => {
        const optionTexts = [
          variant.title,
          variant.option1,
          variant.option2,
          variant.option3,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        const sizeMatch = optionTexts.match(/\b(\d+)\s*(pint|pack|ct|count)/i);
        if (sizeMatch) {
          const size = parseInt(sizeMatch[1], 10);
          if (!isNaN(size) && size > 0) {
            sizes.add(size);
          }
        }
      });
      this.packSizes = Array.from(sizes).sort((a, b) => a - b);
    }
  }
  init() {
    document.addEventListener("addToPack", this.handleAddToPack.bind(this));
    document.addEventListener("flavor-picker:loaded", () => {
      this.isFlavorPickerActive = true;
      this.syncWithFlavorPicker();
    });
    document.addEventListener("pack:updated", (event) => {
      if (this.isFlavorPickerActive && event.detail.pack) {
        this.state = {
          ...this.state,
          ...event.detail.pack
        };
        this.saveState();
      }
    });
    if (this.state.items.length > 0 && !this.isOnPackBuilder()) {
      const stateCollectionId = String(this.state.collectionId || '');
      const currentCollectionId = String(this.getCollectionId() || '');
      const currentProductVariants = this.currentProductVariantIds;
      const isProductInPack = currentProductVariants.length > 0 && 
        this.state.items.some(item => currentProductVariants.includes(String(item.id)));
      if (isProductInPack || (currentCollectionId && stateCollectionId === currentCollectionId)) {
        this.showPackNotification();
      }
    }
    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape") {
        const notification = document.querySelector(
          ".pack-manager-notification"
        );
        if (notification) {
          this.hideNotification();
        }
      }
    });
  }
  loadState() {
    try {
      const currentCollectionId = this.getCollectionId();
      const currentCollectionHandle = this.getCollectionHandle();
      const currentProductVariants = this.currentProductVariantIds;
      const keys = Object.keys(localStorage);
      const packKeys = keys.filter((key) => key.startsWith(this.PACK_KEY_BASE));
      for (const packKey of packKeys) {
        try {
          const pack = JSON.parse(localStorage.getItem(packKey));
          if (pack && (pack.activeSize || pack.items?.length > 0)) {
            // Check if current product is in this pack
            if (currentProductVariants.length > 0 && pack.items) {
              const hasCurrentProduct = pack.items.some(item => 
                currentProductVariants.includes(String(item.id))
              );
              if (hasCurrentProduct) {
                this.currentPackKey = packKey;
                return pack;
              }
            }
            // Check if this pack belongs to current collection
            const keyParts = packKey.split('_');
            const keyCollectionId = keyParts[keyParts.length - 1];
            if (keyCollectionId === String(currentCollectionId) || 
                packKey.includes(`_${currentCollectionHandle}`) ||
                (pack.collectionId && String(pack.collectionId) === String(currentCollectionId))) {
              this.currentPackKey = packKey;
              return pack;
            }
          }
        } catch (e) {
          console.error("Error parsing pack key:", packKey, e);
        }
      }
    } catch (error) {
      console.error("Error loading pack state:", error);
    }
    return {
      activeSize: null,
      items: [],
      sizes: [],
      isGiftPack: false,
      collectionId: this.getCollectionId(),
      collectionHandle: this.getCollectionHandle()
    };
  }
  saveState() {
    try {
      if (!this.state.collectionId) {
        this.state.collectionId = this.getCollectionId();
      }
      if (!this.state.collectionHandle) {
        this.state.collectionHandle = this.getCollectionHandle();
      }
      const collectionPart = this.state.collectionId
        ? `_${this.state.collectionId}`
        : `_${this.state.collectionHandle}`;
      const keyToUse =
        this.currentPackKey || `${this.PACK_KEY_BASE}${collectionPart}`;
      localStorage.setItem(keyToUse, JSON.stringify(this.state));
      this.currentPackKey = keyToUse;
    } catch (error) {
      console.error("Error saving pack state:", error);
    }
  }
  handleAddToPack(event) {
    if (this.isFlavorPickerActive || !this.getCollectionId()) {
      return;
    }
    const { productId, title, quantity, imageUrl, imageUrlLifestyle, packOutOrderSkuNexus, sku } = event.detail;
    this.state = this.loadState();
    if (!this.state.activeSize) {
      const packSizes = this.packSizes.length > 0 ? this.packSizes : [6, 12];
      if (packSizes.length === 1) {
        this.selectPackSize(packSizes[0]);
        this.addItemToPack(productId, title, quantity, imageUrl, imageUrlLifestyle, packOutOrderSkuNexus, sku);
        return;
      }
      this.showPackSizeSelection(productId, title, quantity, imageUrl, imageUrlLifestyle, packOutOrderSkuNexus, sku);
      return;
    }
    const currentTotal = this.getTotalItems();
    if (currentTotal === this.state.activeSize) {
      const largerSizes =
        this.state.sizes?.filter((size) => size > this.state.activeSize) || [];
      if (largerSizes.length > 0) {
        this.showUpgradePackNotification(
          productId,
          title,
          quantity,
          imageUrl,
          imageUrlLifestyle,
          largerSizes,
          packOutOrderSkuNexus,
          sku
        );
      } else {
        this.showPackFullNotification();
      }
      return;
    }
    if (currentTotal + quantity > this.state.activeSize) {
      const largerSizes =
        this.state.sizes?.filter((size) => size > this.state.activeSize) || [];
      if (largerSizes.length > 0) {
        this.showUpgradePackNotification(
          productId,
          title,
          quantity,
          imageUrl,
          imageUrlLifestyle,
          largerSizes,
          packOutOrderSkuNexus,
          sku
        );
      } else {
        this.showPackFullNotification();
      }
      return;
    }
    this.addItemToPack(productId, title, quantity, imageUrl, imageUrlLifestyle, packOutOrderSkuNexus, sku);
    this.showAddToPackConfirmation(title, quantity);
  }
  addItemToPack(productId, title, quantity, imageUrl, imageUrlLifestyle, packOutOrderSkuNexus, sku) {
    const variantId = productId.replace("gid://shopify/ProductVariant/", "");
    const existingIndex = this.state.items.findIndex(
      (item) => item.id === variantId
    );
    if (existingIndex >= 0) {
      this.state.items[existingIndex].quantity += quantity;
      if (imageUrl && !this.state.items[existingIndex].imageUrl) {
        this.state.items[existingIndex].imageUrl = imageUrl;
      }
      if (imageUrlLifestyle && !this.state.items[existingIndex].imageUrlLifestyle) {
        this.state.items[existingIndex].imageUrlLifestyle = imageUrlLifestyle;
      }
    } else {
      this.state.items.push({
        id: variantId,
        imageUrl,
        imageUrlLifestyle,
        packOutOrderSkuNexus,
        quantity,
        sku,
        title,
      });
    }
    this.saveState();
    this.updateFloatingNotification();
  }
  showAddToPackConfirmation(title, quantity) {
    this.showToast(`Added ${quantity}× ${title} to your pack`);
    if (!document.querySelector(".pack-manager-floating-notification")) {
      this.showPackNotification();
    }
  }
  showToast(message) {
    document
      .querySelectorAll(".pack-manager-toast")
      .forEach((toast) => toast.remove());
    const toast = document.createElement("div");
    toast.className = "pack-manager-toast";
    toast.setAttribute("role", "alert");
    toast.setAttribute("aria-live", "assertive");
    toast.textContent = message;
    document.body.appendChild(toast);
    setTimeout(() => toast.classList.add("show"), 10);
    setTimeout(() => {
      toast.classList.remove("show");
      setTimeout(() => toast.remove(), 300);
    }, 3000);
  }
  showPackNotification() {
    const notification = document.createElement("div");
    notification.className = "pack-manager-floating-notification";
    notification.innerHTML = `
      <button 
        class="pack-floating-toggle" 
        onclick="window.packManager.togglePackDetails()"
        aria-label="View pack contents: ${this.getTotalItems()} items"
        aria-expanded="false"
        aria-controls="pack-details-dialog"
        type="button"
      >
        <img src="${
          window.packManagerLogo
        }" class="pack-floating-icon" alt="" aria-hidden="true" />
        <span class="pack-floating-count" aria-hidden="true">${this.getTotalItems()}</span>
      </button>
    `;
    document.body.appendChild(notification);
  }
  hideNotification() {
    clearTimeout(this.notificationTimeout);
    const notifications = document.querySelectorAll(
      ".pack-manager-notification"
    );
    notifications.forEach((notification) => {
      notification.classList.remove("show");
      setTimeout(() => notification.remove(), 300);
    });
  }
  togglePackDetails() {
    const existingNotification = document.querySelector(
      ".pack-manager-notification"
    );
    const toggleButton = document.querySelector(".pack-floating-toggle");
    if (existingNotification) {
      this.hideNotification();
      if (toggleButton) {
        toggleButton.setAttribute("aria-expanded", "false");
      }
      return;
    }
    if (toggleButton) {
      toggleButton.setAttribute("aria-expanded", "true");
    }
    this.showDetailedPackView();
  }
  showDetailedPackView() {
    const totalItems = this.getTotalItems();
    const remainingSlots = this.state.activeSize - totalItems;
    this.hideNotification();
    const notification = document.createElement("div");
    notification.className = "pack-manager-notification pack-details-view";
    notification.setAttribute("id", "pack-details-dialog");
    notification.setAttribute("role", "dialog");
    notification.setAttribute("aria-label", "Pack details");
    notification.setAttribute("aria-modal", "false");
    notification.innerHTML = `
      <div class="pack-notification-content">
        <div class="pack-notification-header">
          <h2 id="pack-dialog-title" class="heading h4">YOUR PACK</h2>
          <button 
            class="pack-notification-close" 
            onclick="window.packManager.hideNotification()"
            aria-label="Close pack details"
            type="button"
          >
            <span aria-hidden="true">×</span>
          </button>
        </div>
        <div class="pack-notification-items-list" role="list" aria-label="Pack items">
          ${this.state.items
            .map(
              (item) => `
            <div class="pack-notification-item-row" role="listitem">
              <span>${item.quantity}× ${item.title}</span>
              <button 
                class="pack-notification-remove" 
                onclick="window.packManager.removeItem('${item.id}')" 
                aria-label="Remove ${item.quantity} ${item.title} from pack"
                type="button"
              >
                <span aria-hidden="true">×</span>
              </button>
            </div>
          `
            )
            .join("")}
        </div>
        <div class="pack-notification-progress">
          <div class="pack-slots">
            ${this.renderPackSlots()}
          </div>
          <div class="pack-progress-text" role="status" aria-live="polite">
            ${totalItems} of ${this.state.activeSize} slots filled
            ${
              remainingSlots > 0
                ? `• ${remainingSlots} remaining`
                : "• Pack Complete!"
            }
          </div>
        </div>
        <div class="pack-notification-actions">
          ${
            remainingSlots > 0
              ? `
            <a href="${this.PACK_BUILDER_URL}" class="button button--secondary button--full button--small">
              Build Your Pack
            </a>
            <button class="button button--outline button--full button--small" onclick="window.packManager.hideNotification()">
              Continue Shopping
            </button>
          `
              : `
            <a href="${this.PACK_BUILDER_URL}" class="button button--secondary button--small button--full">
              Review & Checkout
            </a>
          `
          }
        </div>
      </div>
    `;
    document.body.appendChild(notification);
    setTimeout(() => {
      notification.classList.add("show");
      const closeButton = notification.querySelector(
        ".pack-notification-close"
      );
      if (closeButton) closeButton.focus();
    }, 10);
  }
  removeItem(itemId) {
    const removedItem = this.state.items.find((item) => item.id === itemId);
    this.state.items = this.state.items.filter((item) => item.id !== itemId);
    this.saveState();
    if (removedItem) {
      this.showToast(`Removed ${removedItem.title} from your pack`);
    }
    if (this.state.items.length === 0) {
      document.querySelector(".pack-manager-floating-notification")?.remove();
      this.hideNotification();
    } else {
      this.updateFloatingNotification();
      this.updateTopNotification();
    }
  }
  updateFloatingNotification() {
    const floatingNotification = document.querySelector(
      ".pack-manager-floating-notification"
    );
    if (
      !floatingNotification &&
      this.state.items.length > 0 &&
      !this.isOnPackBuilder()
    ) {
      this.showPackNotification();
      return;
    }
    const count = document.querySelector(".pack-floating-count");
    const toggleButton = document.querySelector(".pack-floating-toggle");
    const totalItems = this.getTotalItems();
    if (count) count.textContent = totalItems;
    if (toggleButton) {
      toggleButton.setAttribute(
        "aria-label",
        `View pack contents: ${totalItems} items`
      );
    }
  }
  updateTopNotification() {
    const detailsView = document.querySelector(
      ".pack-manager-notification.pack-details-view"
    );
    if (detailsView) {
      this.showDetailedPackView();
    }
  }
  getTotalItems() {
    return this.state.items.reduce((total, item) => total + item.quantity, 0);
  }
  getPackSummary() {
    const total = this.getTotalItems();
    const itemTypes = this.state.items.length;
    return `${total} item${total !== 1 ? "s" : ""} from ${itemTypes} flavor${
      itemTypes !== 1 ? "s" : ""
    }`;
  }
  isOnPackBuilder() {
    return (
      window.location.pathname.includes(this.getCollectionHandle()) ||
      document.querySelector(".flavor-picker-container")
    );
  }
  syncWithFlavorPicker() {
    if (window.flavorPickerInstance) {
      document.dispatchEvent(
        new CustomEvent("pack-manager:sync", {
          detail: { pack: this.state },
        })
      );
    }
  }
  clearPack() {
    if (this.currentPackKey) {
      localStorage.removeItem(this.currentPackKey);
    }
    this.state = {
      activeSize: null,
      items: [],
      sizes: [],
      isGiftPack: false,
      collectionId: this.getCollectionId(),
      collectionHandle: this.getCollectionHandle()
    };
    document.querySelector(".pack-manager-floating-notification")?.remove();
    this.hideNotification();
  }
  showPackFullNotification() {
    this.showToast(
      `Your ${this.state.activeSize} pack is full! Click the pack icon to review.`
    );
    if (!document.querySelector(".pack-manager-floating-notification")) {
      this.showPackNotification();
    }
  }
  showUpgradePackNotification(
    productId,
    title,
    quantity,
    imageUrl,
    imageUrlLifestyle,
    largerSizes,
    packOutOrderSkuNexus,
    sku
  ) {
    const nextSize = largerSizes[0];
    this.pendingItem = { imageUrl, imageUrlLifestyle, packOutOrderSkuNexus, productId, quantity, sku, title };
    this.hideNotification();
    const notification = document.createElement("div");
    notification.className = "pack-manager-notification pack-upgrade-prompt";
    notification.innerHTML = `
      <div class="pack-notification-content">
        <div class="pack-notification-header">
          <h2 id="pack-dialog-title">PACK IS FULL</h2>
          <button 
            class="pack-notification-close" 
            onclick="window.packManager.hideNotification()"
            aria-label="Close pack details"
            type="button"
          >
            <span aria-hidden="true">×</span>
          </button>
        </div>
        <div class="pack-notification-message">
          <p>Your ${
            this.state.activeSize
          } pack is full. Upgrade to continue adding items?</p>
          <div class="pack-slots">
            ${this.renderPackSlots()}
          </div>
        </div>
        <div class="pack-notification-actions">
          <button class="button button--secondary" onclick="window.packManager.hideNotification()">
            Keep ${this.state.activeSize} Pack
          </button>
          <button class="button button--primary" onclick="window.packManager.upgradePackSize(${nextSize})">
            Upgrade to ${nextSize} Pack
          </button>
        </div>
      </div>
    `;
    document.body.appendChild(notification);
    setTimeout(() => notification.classList.add("show"), 10);
  }
  upgradePackSize(newSize) {
    this.state.activeSize = newSize;
    this.saveState();
    this.hideNotification();
    if (this.pendingItem) {
      const { imageUrl, imageUrlLifestyle, packOutOrderSkuNexus, productId, quantity, sku, title } = this.pendingItem;
      this.addItemToPack(productId, title, quantity, imageUrl, imageUrlLifestyle, packOutOrderSkuNexus, sku);
      this.showToast(
        `Pack upgraded to ${newSize}! Added ${quantity}× ${title}`
      );
      this.pendingItem = null;
    } else {
      this.showToast(`Pack upgraded to ${newSize} items`);
    }
    this.updateFloatingNotification();
  }
  renderPackSlots() {
    const slots = [];
    let filledCount = 0;
    this.state.items.forEach((item) => {
      for (let i = 0; i < item.quantity; i++) {
        slots.push(
          `<div class="pack-slot filled" title="${item.title}"></div>`
        );
        filledCount++;
      }
    });
    for (let i = filledCount; i < this.state.activeSize; i++) {
      slots.push(`<div class="pack-slot empty"></div>`);
    }
    return slots.join("");
  }
  showPackSizeSelection(productId, title, quantity, imageUrl, imageUrlLifestyle, packOutOrderSkuNexus, sku) {
    this.pendingItem = { imageUrl, imageUrlLifestyle, packOutOrderSkuNexus, productId, quantity, sku, title };
    const packSizes = this.packSizes.length > 0 ? this.packSizes : [6, 12];
    const notification = document.createElement("div");
    notification.className = "pack-manager-notification pack-size-selection";
    notification.innerHTML = `
      <div class="pack-notification-content">
        <div class="pack-notification-header">
          <h2 id="pack-dialog-title">CREATE YOUR PACK</h2>
          <button 
            class="pack-notification-close" 
            onclick="window.packManager.hideNotification()"
            aria-label="Close pack details"
            type="button"
          >
            <span aria-hidden="true">×</span>
          </button>
        </div>
        <div class="pack-size-selection-content">
          <div class="pack-option-header">
            SAVE $40 ON 12 PINT PACKS
            <span class="sale-price">$129.95</span>
            <span class="original-price">$169.90</span>
          </div>
          <p class="pack-selection-subtitle">Select your pack size:</p>
          <div class="pack-size-options">
            ${packSizes
              .map(
                (size) => `
              <button class="pack-size-option" onclick="window.packManager.selectPackSize(${size})">
                <span class="pack-size-count">${size}</span>
                <span class="pack-size-label">PACK</span>
              </button>
            `
              )
              .join("")}
          </div>
          <div class="pack-notification-footer">
            <p class="pack-notification-note">Minimum of 6 flavors required to ship</p>
          </div>
        </div>
      </div>
    `;
    document.body.appendChild(notification);
    setTimeout(() => notification.classList.add("show"), 10);
  }
  selectPackSize(size) {
    this.state.activeSize = size;
    this.state.sizes = this.packSizes.length > 0 ? this.packSizes : [6, 12];
    this.state.isGiftPack = false;
    this.state.collectionId = this.getCollectionId();
    this.state.collectionHandle = this.getCollectionHandle();
    if (!this.currentPackKey) {
      const collectionPart = this.collectionId
        ? `_${this.collectionId}`
        : `_${this.getCollectionHandle()}`;
      this.currentPackKey = `${this.PACK_KEY_BASE}${collectionPart}`;
    }
    this.saveState();
    this.hideNotification();
    if (this.pendingItem) {
      const { imageUrl, imageUrlLifestyle, packOutOrderSkuNexus, productId, quantity, sku, title } = this.pendingItem;
      this.addItemToPack(productId, title, quantity, imageUrl, imageUrlLifestyle, packOutOrderSkuNexus, sku);
      this.showToast(`Started ${size} pack with ${quantity}× ${title}`);
      this.pendingItem = null;
    }
    this.showPackNotification();
  }
}
document.addEventListener("DOMContentLoaded", () => {
  window.packManager = new PackManager();
});
